const EURO_RATE_KEY = 'matata-usd-eur-rate';
const EURO_RATE_TTL = 12 * 60 * 60 * 1000;
const SHOPIFY_CURRENCY_SCRIPT = 'https://cdn.shopify.com/s/javascripts/currencies.js';

let eurReferenceRenderToken = 0;

function formatEurAmount(amount) {
  const [whole, fraction] = amount.toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${grouped},${fraction}`;
}

function readCachedRate() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(EURO_RATE_KEY) || 'null');
    if (cached && Date.now() - cached.savedAt < EURO_RATE_TTL && cached.rate > 0) {
      return cached.rate;
    }
  } catch (_) {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  return null;
}

function cacheRate(rate) {
  try {
    sessionStorage.setItem(EURO_RATE_KEY, JSON.stringify({ rate, savedAt: Date.now() }));
  } catch (_) {
    // The estimate still works when the rate cannot be cached.
  }
}

async function readJsonRate(url, readRate) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('EUR rate request failed');
  const data = await response.json();
  const rate = Number(readRate(data));
  if (!Number.isFinite(rate) || rate <= 0) throw new Error('EUR rate missing');
  return rate;
}

function loadShopifyUsdToEurRate() {
  const readRate = () => {
    const rates = window.Currency && window.Currency.rates;
    const usd = Number(rates && rates.USD);
    const eur = Number(rates && rates.EUR);
    if (!usd || !eur) throw new Error('Shopify EUR rate missing');
    return usd / eur;
  };

  if (window.Currency && window.Currency.rates) {
    return Promise.resolve(readRate());
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SHOPIFY_CURRENCY_SCRIPT}"]`);
    const script = existing || document.createElement('script');

    const finish = () => {
      try {
        resolve(readRate());
      } catch (error) {
        reject(error);
      }
    };

    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Shopify currency script failed')), { once: true });

    if (!existing) {
      script.src = SHOPIFY_CURRENCY_SCRIPT;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

async function getUsdToEurRate() {
  const cachedRate = readCachedRate();
  if (cachedRate) return cachedRate;

  const sources = [
    () =>
      readJsonRate('https://open.er-api.com/v6/latest/USD', (data) => data && data.rates && data.rates.EUR),
    () =>
      readJsonRate(
        'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json',
        (data) => data && data.usd && data.usd.eur
      ),
    () =>
      readJsonRate('https://api.frankfurter.app/latest?from=USD&to=EUR', (data) => data && data.rates && data.rates.EUR),
    () => loadShopifyUsdToEurRate(),
  ];

  let lastError;
  for (const source of sources) {
    try {
      const rate = await source();
      cacheRate(rate);
      return rate;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('EUR rate unavailable');
}

function readManualRate(node) {
  const manualRate = Number(node.dataset.manualRate);
  if (!Number.isFinite(manualRate) || manualRate <= 0) {
    throw new Error('Invalid manual EUR rate');
  }
  return manualRate;
}

async function resolveRate(node) {
  const mode = (node.dataset.rateMode || 'automatic').toLowerCase();
  if (mode === 'manual') return readManualRate(node);

  try {
    return await getUsdToEurRate();
  } catch (_) {
    return readManualRate(node);
  }
}

function paintEurReference(node, rate) {
  const cents = Number(node.dataset.usdCents);
  const value = node.querySelector('.price-eur-reference__value');
  if (!value || !Number.isFinite(cents) || !Number.isFinite(rate) || rate <= 0) {
    node.hidden = true;
    return;
  }

  value.textContent = `≈ € ${formatEurAmount((cents / 100) * rate)} EUR`;
  node.hidden = false;
}

async function renderEurReferencePrices() {
  const renderToken = ++eurReferenceRenderToken;
  const nodes = document.querySelectorAll('.price-eur-reference[data-usd-cents]');
  if (!nodes.length) return;

  await Promise.all(
    [...nodes].map(async (node) => {
      try {
        const rate = await resolveRate(node);
        if (renderToken !== eurReferenceRenderToken) return;
        paintEurReference(node, rate);
      } catch (_) {
        node.hidden = true;
      }
    })
  );
}

if (!window.__matataEurReferenceInit) {
  window.__matataEurReferenceInit = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderEurReferencePrices, { once: true });
  } else {
    renderEurReferencePrices();
  }
  document.addEventListener('shopify:section:load', renderEurReferencePrices);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, renderEurReferencePrices);
  }
}
